<script setup lang="ts">
import { ref, computed } from 'vue'
import { QMediaPlayer } from '@quasar/quasar-ui-qmediaplayer'
import type { PreviewIncidentReportResponse } from '@roadfootage/api-client'

const props = defineProps<{
  incident: PreviewIncidentReportResponse
}>()

type MediaItem = {
  id: string
  type: 'video' | 'image'
  thumbnailUrl: string | null
  previewUrl: string | null
}

const activeMediaId = ref<string | null>(null)

const mediaItems = computed<MediaItem[]>(() => [
  ...(props.incident.footageCollection ?? []).map((f) => ({
    id: f.id!,
    type: 'video' as const,
    thumbnailUrl: f.thumbnailUrl ?? null,
    previewUrl: f.previewUrl ?? null
  })),
  ...(props.incident.imageCollection ?? []).map((img) => ({
    id: img.id!,
    type: 'image' as const,
    thumbnailUrl: img.thumbnailUrl ?? null,
    previewUrl: img.previewUrl ?? null
  }))
])

const activeMedia = computed<MediaItem | null>(() => {
  const targetId = activeMediaId.value ?? props.incident.primaryUserIncidentFileId
  return mediaItems.value.find((m) => m.id === targetId) ?? mediaItems.value[0] ?? null
})

function selectMedia(id: string) {
  activeMediaId.value = id
}
</script>

<template>
  <q-card>
    <q-banner v-if="incident.hasContentWarning" class="bg-negative text-white" dense>
      <template #avatar>
        <q-icon name="mdi-alert" />
      </template>
      This incident contains content that some viewers may find distressing.
    </q-banner>

    <q-responsive :ratio="16 / 9">
      <!-- Video -->
      <QMediaPlayer
        v-if="activeMedia?.type === 'video' && activeMedia.previewUrl"
        :key="activeMedia.previewUrl"
        :source="activeMedia.previewUrl"
        type="video"
        class="full-width full-height"
        :controls-display-time="3000"
        dense
        hide-volume-slider
        hide-settings-btn
      />

      <!-- Image -->
      <q-img
        v-else-if="activeMedia?.type === 'image' && activeMedia.previewUrl"
        :src="activeMedia.previewUrl"
        fit="contain"
        class="full-width full-height bg-grey-10"
      >
        <template #error>
          <div class="flex flex-center column text-grey-5 q-gutter-y-sm">
            <q-icon name="mdi-image-broken" size="lg" />
            <div class="text-caption">Image unavailable</div>
          </div>
        </template>
      </q-img>

      <!-- No media -->
      <div
        v-else
        class="flex flex-center column text-grey-5 q-gutter-y-sm bg-grey-3 full-width full-height"
      >
        <q-icon name="mdi-video-off" size="xl" />
        <div class="text-caption">No media available</div>
      </div>
    </q-responsive>

    <!-- Thumbnail selector (only shown when more than one file) -->
    <template v-if="mediaItems.length > 1">
      <q-separator />
      <q-scroll-area style="height: 115px" class="q-pa-sm">
        <div class="row no-wrap q-gutter-sm">
          <q-card
            v-for="item in mediaItems"
            :key="item.id"
            flat
            bordered
            class="thumbnail-card cursor-pointer"
            :class="{ 'thumbnail-card--active': activeMedia?.id === item.id }"
            @click="selectMedia(item.id)"
          >
            <q-img :src="item.thumbnailUrl ?? undefined" fit="cover" class="thumbnail-image">
              <template #error>
                <div class="fit flex flex-center bg-grey-3">
                  <q-icon
                    :name="item.type === 'video' ? 'mdi-video' : 'mdi-image'"
                    color="grey-5"
                  />
                </div>
              </template>
            </q-img>
            <q-separator />
            <div class="q-pa-xs text-center text-caption text-grey-7">
              {{ item.type === 'video' ? 'Video' : 'Image' }}
            </div>
          </q-card>
        </div>
      </q-scroll-area>
    </template>
  </q-card>
</template>

<style lang="scss" scoped>
.thumbnail-card {
  flex-shrink: 0;
  width: 110px;
  border: 2px solid transparent !important;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--q-primary) !important;
  }

  &--active {
    border-color: var(--q-primary) !important;
  }
}

.thumbnail-image {
  height: 62px;
}
</style>
